import type {
    FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IQuestionnaireActionsProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    QuestionnairePartContentProps
} from "../../type";
import {
    ArrowLeft as ArrowLeftIcon
} from "lucide-react-native";
import {
    getMirrorStyle
} from "../../../../helpers/direction";
import {
    QUESTIONNAIRE_BUTTON_SIZES
} from "../../stylesheet";
import {
    withQuestionnaireContext
} from "../../context";
import Button from "../../../button";
import Card from "../../../card";
import Text from "../../../text";
import Kbd from "../../../kbd";

const QuestionnaireBackIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => {
    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    return <ArrowLeftIcon
        style={getMirrorStyle({
            direction
        })}
        color={customColor}
        size={size}
    />;
};

const QuestionnaireActionsContent: FC<QuestionnairePartContentProps<IQuestionnaireActionsProps>> = ({
    customTheme: customThemeProp,
    isDivided,
    children,
    context,
    style,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context.customLocalize);

    const pointerType = usePointerType();

    const {
        hint: hintDynamicStyle
    } = useStyles({
        spaces
    });

    const isLarge = context.size === "large";

    const isLast = context.activeIndex >= context.items.length - 1;

    const isBackVisible = context.isReview || context.activeIndex > 0;

    const isSkipVisible = !context.isReview && !!context.activeItem && !context.activeItem.isRequired;

    const isHintVisible = !context.isReview && Platform.OS === "web" && pointerType === "fine";

    const getPrimaryTitle = () => {
        if(context.isReview) {
            return context.isSubmitFailed ? localize("try-again") : localize("questionnaire-submit");
        }

        if(context.isReturning || isLast && context.isReviewEnabled) {
            return localize("questionnaire-review");
        }

        return isLast ? localize("questionnaire-submit") : localize("questionnaire-next");
    };

    const renderBack = () => {
        if(!isBackVisible) {
            return null;
        }

        return <Button
            accessibilityLabel={isLarge ? localize("questionnaire-previous-question") : undefined}
            title={isLarge ? undefined : localize("go-back")}
            size={QUESTIONNAIRE_BUTTON_SIZES[context.size]}
            isDisabled={context.isLoading}
            onPress={context.handleBack}
            icon={QuestionnaireBackIcon}
            customTheme={customTheme}
            variant="ghost"
            type="neutral"
            key="back"
        />;
    };

    const renderHint = () => {
        if(!isHintVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.hint,
                hintDynamicStyle
            ]}
            key="hint"
        >
            <Kbd
                customLocalize={context.customLocalize}
                customTheme={customTheme}
                aria-hidden={true}
                keys="enter"
                size="small"
            />
            <Text
                customTheme={customTheme}
                variant="bodySmallSize"
                color="low"
            >
                {localize("questionnaire-enter-hint")}
            </Text>
        </View>;
    };

    const renderSkip = () => {
        if(!isSkipVisible) {
            return null;
        }

        return <Button
            size={QUESTIONNAIRE_BUTTON_SIZES[context.size]}
            title={localize("questionnaire-skip")}
            isDisabled={context.isLoading}
            onPress={context.handleSkip}
            customTheme={customTheme}
            variant="ghost"
            type="neutral"
            key="skip"
        />;
    };

    const renderDefault = () => {
        return <>
            {renderBack()}
            {renderHint()}
            {
                isLarge ?
                    null
                :
                    <View
                        style={stylesheet.spacer}
                    />
            }
            {renderSkip()}
            <Button
                style={isLarge ? stylesheet.stretched : undefined}
                size={QUESTIONNAIRE_BUTTON_SIZES[context.size]}
                isDisabled={context.isDisabled}
                isLoading={context.isLoading}
                onPress={context.handleNext}
                customTheme={customTheme}
                title={getPrimaryTitle()}
                variant="filled"
                type="primary"
            />
        </>;
    };

    return <Card.Footer
        {...props}
        customTheme={customTheme}
        isDivided={isDivided}
        style={style}
    >
        {children ?? renderDefault()}
    </Card.Footer>;
};
export default Object.assign(withQuestionnaireContext("Actions", QuestionnaireActionsContent), {
    cardPart: "footer" as const
});
